import { useState, useEffect, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import FormInput from "../../../../components/Form/FormInput";
import FormSelect from "../../../../components/Form/FormSelect";
import FormTextarea from "../../../../components/Form/TextArea";
import DateTimeField from "../../../../components/Form/DateTimeField";
import TagMultiSelect from "../../../../components/Form/TagMultiSelect";
import TextEditor from "../../../../components/Form/TextEditor";
import { AuthContext } from "../../../../context/AuthContext";
import { isFreelance, isProjectManager } from "../../../../utils/constants/accountType";
import { pdfTemplateOptions, DEFAULT_PDF_TEMPLATE_ID } from "../../../../utils/constants/pdfTemplates";
import { QUOTATION_STATUSES } from "../../../../utils/constants/quotationStatus";
import { clientName, personName, formatLanguagePair, toLocalInput } from "../quotationFormat";
import { btn } from "../../../../components/Button/buttonStyles";

const nowLocal = () => toLocalInput(new Date());

// An emptied editor still returns markup such as "<p></p>".
const hasText = (html) => Boolean(html && html.replace(/<[^>]*>/g, "").trim());

const EMPTY_FORM = {
  client_id: "",
  client_contact_person_id: "",
  quotation_name: "",
  language_pair_ids: [],
  specialization_id: "",
  start_at: "",
  deadline_at: "",
  instructions: "",
  expire_at: "",
  pdf_template_id: String(DEFAULT_PDF_TEMPLATE_ID),
  internal_note: "",
  primary_manager_id: "",
  status: "",
};

const fromQuotation = (q) => ({
  client_id: String(q.client_id),
  client_contact_person_id: q.client_contact_person_id ? String(q.client_contact_person_id) : "",
  quotation_name: q.quotation_name || "",
  language_pair_ids: (q.languagePairs || []).map((lp) => lp.id),
  specialization_id: q.specialization_id ? String(q.specialization_id) : "",
  start_at: toLocalInput(q.start_at),
  deadline_at: toLocalInput(q.deadline_at),
  instructions: q.instructions || "",
  expire_at: toLocalInput(q.expire_at),
  pdf_template_id: String(q.pdf_template_id),
  internal_note: q.internal_note || "",
  primary_manager_id: q.primary_manager_id ? String(q.primary_manager_id) : "",
  status: q.status,
});

const QuotationForm = ({ quotationId = null }) => {
  const isEdit = Boolean(quotationId);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  // Freelance accounts have no managers, so the field is hidden and not required.
  const freelance = isFreelance(user);
  const projectManager = isProjectManager(user);
  const [searchParams] = useSearchParams();
  const presetClientId = searchParams.get("clientId") || "";

  const [form, setForm] = useState(null);
  // Accepted and linked to a project: only name, PDF template and internal note.
  const [locked, setLocked] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [clients, setClients] = useState([]);
  const [contactPersons, setContactPersons] = useState([]);
  const [languagePairs, setLanguagePairs] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [managers, setManagers] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [clientsRes, pairsRes, specsRes, managersRes, settingsRes, quotationRes] =
          await Promise.all([
            api.get("/clients"),
            api.get("/admin-language-pairs"),
            api.get("/admin-specializations"),
            // The full manager module is admin-only; project managers read the trimmed roster.
            freelance ? Promise.resolve(null) : api.get(projectManager ? "/assignable-managers" : "/managers"),
            isEdit ? Promise.resolve(null) : api.get("/account-settings").catch(() => null),
            isEdit ? api.get(`/quotations/${quotationId}`) : Promise.resolve(null),
          ]);

        setClients(clientsRes.data.data || []);
        setLanguagePairs(pairsRes.data.data || []);
        setSpecializations((specsRes.data.data || []).filter((s) => s.active_flag === true));
        setManagers(managersRes?.data?.data || []);

        if (isEdit) {
          setForm(fromQuotation(quotationRes.data.data));
          setLocked(Boolean(quotationRes.data.data.locked));
          return;
        }

        const defaultTemplate = settingsRes?.data?.data?.settings?.default_quote_template_id;
        const selfManagerId = managersRes?.data?.meta?.self_manager_id;
        setForm({
          ...EMPTY_FORM,
          client_id: presetClientId,
          start_at: nowLocal(),
          pdf_template_id: String(defaultTemplate || DEFAULT_PDF_TEMPLATE_ID),
          primary_manager_id: selfManagerId ? String(selfManagerId) : "",
        });
      } catch (err) {
        console.error("Failed to load quotation form:", err);
        setServerError(err.response?.data?.message || "Failed to load the form.");
      }
    };

    load();
  }, [isEdit, quotationId, freelance, projectManager, presetClientId]);

  useEffect(() => {
    if (!form?.client_id) {
      setContactPersons([]);
      return;
    }

    api
      .get(`/client/contact-persons/${form.client_id}`)
      .then((res) => setContactPersons(res.data.data || []))
      .catch(() => setContactPersons([]));
  }, [form?.client_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      // A contact person belongs to one client.
      ...(name === "client_id" ? { client_contact_person_id: "" } : {}),
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setServerError("");
  };

  const validate = () => {
    const err = {};
    if (!form.quotation_name.trim()) err.quotation_name = "Name is required.";
    if (!form.pdf_template_id) err.pdf_template_id = "PDF template is required.";

    if (locked) {
      setErrors(err);
      return Object.keys(err).length === 0;
    }

    if (!form.client_id) err.client_id = "Client is required.";
    if (!freelance && !form.primary_manager_id)
      err.primary_manager_id = "Primary manager is required.";
    // A project needs these when one is created from the quotation.
    if (!form.language_pair_ids.length)
      err.language_pair_ids = "At least one language pair is required.";
    if (!form.start_at) err.start_at = "Start date is required.";
    if (!form.deadline_at) err.deadline_at = "Delivery date is required.";
    else if (form.start_at && form.deadline_at <= form.start_at)
      err.deadline_at = "Delivery must be after the start time.";

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const buildPayload = () => {
    if (locked) {
      return {
        quotation_name: form.quotation_name.trim(),
        pdf_template_id: Number(form.pdf_template_id),
        internal_note: form.internal_note.trim() || null,
      };
    }

    const payload = {
      client_id: Number(form.client_id),
      client_contact_person_id: form.client_contact_person_id ? Number(form.client_contact_person_id) : null,
      quotation_name: form.quotation_name.trim(),
      language_pair_ids: form.language_pair_ids,
      specialization_id: form.specialization_id ? Number(form.specialization_id) : null,
      start_at: form.start_at || null,
      deadline_at: form.deadline_at || null,
      instructions: hasText(form.instructions) ? form.instructions : null,
      expire_at: form.expire_at || null,
      pdf_template_id: Number(form.pdf_template_id),
      internal_note: form.internal_note.trim() || null,
    };
    if (!freelance) payload.primary_manager_id = Number(form.primary_manager_id);
    if (isEdit) payload.status = form.status;
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setServerError("");

    try {
      const res = isEdit
        ? await api.put(`/quotations/${quotationId}`, buildPayload())
        : await api.post("/quotations", buildPayload());

      setSuccess(res.data.message || "Quotation saved.");
      setTimeout(() => navigate(`/quotation/${res.data.data.id}`), 1000);
    } catch (err) {
      console.error("Save quotation error:", err);
      setServerError(err.response?.data?.message || "Failed to save quotation.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!form) {
    return serverError ? (
      <p className="text-danger-600 text-sm">{serverError}</p>
    ) : (
      <div className="flex justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="bg-white shadow rounded-lg p-8 space-y-6">
      {locked && (
        <div className="rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-900">
          This quotation is accepted and linked to a project, so only the name, PDF template and
          internal note can be changed. Move it to Draft to edit the rest.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {!locked && (
        <>
        <FormSelect
          label="Client"
          name="client_id"
          value={form.client_id}
          onChange={handleChange}
          options={clients.map((c) => ({ value: c.id, label: clientName(c) }))}
          error={errors.client_id}
          required
          disabled={!isEdit && Boolean(presetClientId)}
        />

        <FormSelect
          label="Client Contact Person"
          name="client_contact_person_id"
          value={form.client_contact_person_id}
          onChange={handleChange}
          options={contactPersons.map((p) => ({ value: p.id, label: personName(p) }))}
          disabled={!form.client_id}
        />
        </>
        )}

        <div className="md:col-span-2">
          <FormInput
            label="Name"
            name="quotation_name"
            value={form.quotation_name}
            onChange={handleChange}
            error={errors.quotation_name}
            required
          />
        </div>

        {!locked && (
        <>
        <div className="md:col-span-2">
          <TagMultiSelect
            label="Language Pairs"
            required
            value={form.language_pair_ids}
            onChange={(ids) => {
              setForm((prev) => ({ ...prev, language_pair_ids: ids }));
              setErrors((prev) => ({ ...prev, language_pair_ids: "" }));
            }}
            options={languagePairs.map((lp) => ({ value: lp.id, label: formatLanguagePair(lp) }))}
            placeholder="Select language pairs"
            error={errors.language_pair_ids}
            emptyMessage="No language pairs configured"
          />
        </div>

        <FormSelect
          label="Specialization"
          name="specialization_id"
          value={form.specialization_id}
          onChange={handleChange}
          options={specializations.map((s) => ({ value: s.id, label: s.name }))}
        />

        <div className="hidden md:block" />

        <DateTimeField
          label="Start At"
          name="start_at"
          value={form.start_at}
          onChange={handleChange}
          error={errors.start_at}
          required
        />

        <DateTimeField
          label="Delivery At"
          name="deadline_at"
          value={form.deadline_at}
          onChange={handleChange}
          error={errors.deadline_at}
          min={form.start_at}
          required
        />
        </>
        )}
      </div>

      {!locked && (
        <TextEditor
          label="Instructions"
          value={form.instructions}
          onChange={(val) => setForm((prev) => ({ ...prev, instructions: val }))}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {!locked && (
          <DateTimeField
            label="Expire At"
            name="expire_at"
            value={form.expire_at}
            onChange={handleChange}
          />
        )}

        <FormSelect
          label="PDF Template"
          name="pdf_template_id"
          value={form.pdf_template_id}
          onChange={handleChange}
          options={pdfTemplateOptions()}
          error={errors.pdf_template_id}
          required
        />

        {/* Managers are enterprise-only; hidden for freelance accounts. */}
        {!freelance && !locked && (
          <FormSelect
            label="Primary Manager"
            name="primary_manager_id"
            value={form.primary_manager_id}
            onChange={handleChange}
            options={managers.map((m) => ({ value: m.id, label: personName(m) }))}
            error={errors.primary_manager_id}
            required
          />
        )}

        {/* While locked the status only changes through the Draft button. */}
        {isEdit && !locked && (
          <FormSelect
            label="Status"
            name="status"
            value={form.status}
            onChange={handleChange}
            options={QUOTATION_STATUSES}
            required
          />
        )}
      </div>

      <FormTextarea
        label="Internal Note"
        name="internal_note"
        value={form.internal_note}
        onChange={handleChange}
        rows={3}
        placeholder="Internal notes (not visible to client)"
      />

      {serverError && (
        <div className="bg-danger-100 text-danger-700 border border-danger-400 rounded p-3 text-center font-semibold">
          {serverError}
        </div>
      )}

      {success && (
        <div className="bg-green-100 text-green-700 border border-green-400 rounded p-3 text-center font-semibold">
          {success}
        </div>
      )}

      <span className="text-gray-500 text-sm mt-1">
        Fields marked with <span className="text-danger-600">*</span> are mandatory.
      </span>

      <div className="flex gap-4 pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className={btn("primary", "sm")}
        >
          {isSubmitting ? "Saving..." : isEdit ? "Save Changes" : "Create"}
        </button>

        <button
          type="button"
          onClick={() => navigate(isEdit ? `/quotation/${quotationId}` : "/quotations")}
          disabled={isSubmitting}
          className={btn("secondary", "sm")}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default QuotationForm;
